import React from 'react';
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';

interface SustentoSectionProps {
    values: {
        emissionType: string;
        documentType: string;
        authNumber: string;
        sustentoDate: string;
        sustentoNum: string;
    };
    touched: {
        emissionType?: boolean;
        documentType?: boolean;
        authNumber?: boolean;
        sustentoDate?: boolean;
        sustentoNum?: boolean;
    };
    errors: {
        emissionType?: string;
        documentType?: string;
        authNumber?: string;
        sustentoDate?: string;
        sustentoNum?: string;
    };
    onChange: (field: string, value: any) => void;
    onValidate?: () => void;
}

const SustentoSection: React.FC<SustentoSectionProps> = ({
    values,
    touched,
    errors,
    onChange,
    onValidate
}) => {
    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(field, e.target.value);
    };

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3">
            <div className="bg-soft-primary p-3 border-bottom border-light">
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-file-text-line me-2 fs-18"></i>
                    Comprobante de Sustento
                </h5>
            </div>
            <CardBody className="p-4 bg-white">
                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Tipo de emisión
                            </Label>
                            <Input
                                type="select"
                                name="emissionType"
                                className="form-select"
                                value={values.emissionType}
                                onChange={handleInputChange('emissionType')}
                                invalid={!!(touched.emissionType && errors.emissionType)}
                            >
                                <option value="Física">Física</option>
                                <option value="Electrónica">Electrónica</option>
                            </Input>
                            {touched.emissionType && errors.emissionType && (
                                <div className="invalid-feedback">{errors.emissionType}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Documento
                            </Label>
                            <Input
                                type="select"
                                name="documentType"
                                className="form-select"
                                value={values.documentType}
                                onChange={handleInputChange('documentType')}
                                invalid={!!(touched.documentType && errors.documentType)}
                            >
                                <option value="Factura">Factura</option>
                                <option value="Nota de Venta">Nota de Venta</option>
                                <option value="Liquidación">Liquidación</option>
                            </Input>
                            {touched.documentType && errors.documentType && (
                                <div className="invalid-feedback">{errors.documentType}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Buscar por
                            </Label>
                            <div className="input-group">
                                <Input
                                    placeholder="Buscar por: Clave de acceso"
                                    className="form-control"
                                    autoComplete="off"
                                />
                                <Button
                                    color="primary"
                                    className="px-4 fw-bold shadow-sm"
                                    onClick={onValidate}
                                >
                                    <i className="ri-search-line me-1"></i> Validar
                                </Button>
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Clave de acceso
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-key-2-line text-muted"></i>
                                </span>
                                <Input
                                    className="form-control border-start-0 ps-1"
                                    placeholder="Ingrese clave"
                                    autoComplete="off"
                                />
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Número autorización
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-shield-check-line text-muted"></i>
                                </span>
                                <Input
                                    name="authNumber"
                                    className="form-control border-start-0 ps-1"
                                    value={values.authNumber}
                                    onChange={handleInputChange('authNumber')}
                                    invalid={!!(touched.authNumber && errors.authNumber)}
                                    placeholder="1234567890"
                                />
                                {touched.authNumber && errors.authNumber && (
                                    <div className="invalid-feedback">{errors.authNumber}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Fecha de emisión
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-calendar-event-line text-muted"></i>
                                </span>
                                <Input
                                    type="date"
                                    name="sustentoDate"
                                    className="form-control border-start-0 ps-1"
                                    value={values.sustentoDate}
                                    onChange={handleInputChange('sustentoDate')}
                                    invalid={!!(touched.sustentoDate && errors.sustentoDate)}
                                />
                                {touched.sustentoDate && errors.sustentoDate && (
                                    <div className="invalid-feedback">{errors.sustentoDate}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Número comprobante
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-hashtag text-muted"></i>
                                </span>
                                <Input
                                    name="sustentoNum"
                                    className="form-control border-start-0 ps-1"
                                    value={values.sustentoNum}
                                    onChange={handleInputChange('sustentoNum')}
                                    invalid={!!(touched.sustentoNum && errors.sustentoNum)}
                                    placeholder="001-001-000000123"
                                />
                                {touched.sustentoNum && errors.sustentoNum && (
                                    <div className="invalid-feedback">{errors.sustentoNum}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default SustentoSection;
